import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import {
  ArrowLeft, Search, ShoppingCart, X, Plus, Minus,
  ChevronRight, Zap, Clock, Tag, Leaf, Truck,
  Milk, Apple, Wheat, Droplets, Egg, Coffee,
  Package, CheckCircle, Home as HomeIcon,
  ListOrdered, User, MapPin, Loader2, RefreshCw,
  ShoppingBasket, AlertCircle, Store, BadgePercent,
} from "lucide-react";

const G = {
  primary:    "#0c831f",
  light:      "#f3fef5",
  mid:        "#c3f0ca",
  accent:     "#f8cb46",
  white:      "#ffffff",
  bg:         "#f2f3f6",
  text:       "#1c1c1c",
  sub:        "#666666",
  muted:      "#999999",
  border:     "#e8e8e8",
  red:        "#ef4444",
  orange:     "#f97316",
  blue:       "#2563eb",
};

const API = typeof window !== "undefined" && window.location.hostname !== "localhost"
  ? "https://quickry-ivrv.onrender.com/api"
  : "http://localhost:5000/api";

// ── Default emoji → icon map (agar backend icon emoji hai) ────
const EMOJI_COLOR_MAP = {
  "🛒":"#16a34a", "🥤":"#f97316", "💄":"#db2777",
  "🧹":"#2563eb", "👶":"#7c3aed", "🐾":"#92400e",
  "🥬":"#16a34a", "🥛":"#f59e0b", "🍎":"#ef4444",
  "🧈":"#d97706", "🥚":"#f59e0b", "☕":"#92400e",
  "🌾":"#b45309", "🧃":"#0891b2", "🍪":"#ea580c",
};

const DEFAULT_COLORS = ["#16a34a","#f97316","#2563eb","#db2777","#7c3aed","#0891b2","#92400e","#b45309"];

const NAV = [
  { icon:HomeIcon,     label:"Home",    route:"/"            },
  { icon:Search,       label:"Explore", route:"/restaurants" },
  { icon:ShoppingCart, label:"Cart",    route:"/cart", cart:true },
  { icon:ListOrdered,  label:"Orders",  route:"/orders"      },
  { icon:User,         label:"Profile", route:"/profile"     },
];

// ── QTY STEPPER ───────────────────────────────────────────────
function QtyBtn({ qty, onAdd, onDec }) {
  if (qty === 0) return (
    <button onClick={onAdd}
      style={{ width:"100%", height:32, background:G.white, border:`1.5px solid ${G.primary}`, borderRadius:8, display:"flex", alignItems:"center", justifyContent:"center", gap:4, cursor:"pointer" }}>
      <Plus size={13} color={G.primary} strokeWidth={2.5}/>
      <span style={{ color:G.primary, fontWeight:800, fontSize:"0.78rem" }}>ADD</span>
    </button>
  );
  return (
    <div style={{ width:"100%", height:32, background:G.primary, borderRadius:8, display:"flex", alignItems:"center", justifyContent:"space-between", padding:"0 6px" }}>
      <button onClick={onDec} style={{ background:"none", border:"none", cursor:"pointer", display:"flex", padding:2 }}>
        <Minus size={12} color="#fff" strokeWidth={3}/>
      </button>
      <span style={{ color:"#fff", fontWeight:800, fontSize:"0.85rem" }}>{qty}</span>
      <button onClick={onAdd} style={{ background:"none", border:"none", cursor:"pointer", display:"flex", padding:2 }}>
        <Plus size={12} color="#fff" strokeWidth={3}/>
      </button>
    </div>
  );
}

// ── Product Card ──────────────────────────────────────────────
function ProductCard({ p, qty, onAdd, onDec, color="#16a34a" }) {
  const disc = p.mrp > p.price ? Math.round(((p.mrp - p.price) / p.mrp) * 100) : 0;
  const bg   = color + "18";

  return (
    <div className="pcard" style={{ background:G.white, borderRadius:16, overflow:"hidden", border:`1px solid ${G.border}`, transition:"box-shadow .2s" }}>
      {/* Image */}
      <div style={{ position:"relative", background:bg, padding:"14px 14px 6px", display:"flex", alignItems:"center", justifyContent:"center", minHeight:110 }}>
        {p.image ? (
          <img src={p.image} alt={p.name} style={{ width:80, height:80, objectFit:"contain" }}/>
        ) : (
          <div style={{ width:80, height:80, display:"flex", alignItems:"center", justifyContent:"center", opacity:.25 }}>
            <Package size={48} color={color} strokeWidth={1}/>
          </div>
        )}
        {disc > 0 && (
          <div style={{ position:"absolute", top:8, left:8, background:"#f8cb46", borderRadius:6, padding:"2px 6px" }}>
            <span style={{ fontSize:"0.6rem", fontWeight:800, color:"#1a1a1a" }}>{disc}% OFF</span>
          </div>
        )}
        {!p.inStock && (
          <div style={{ position:"absolute", inset:0, background:"rgba(255,255,255,0.75)", display:"flex", alignItems:"center", justifyContent:"center" }}>
            <span style={{ fontSize:"0.7rem", fontWeight:800, color:G.red, background:"#fff", padding:"4px 10px", borderRadius:8, border:`1px solid ${G.red}` }}>Out of Stock</span>
          </div>
        )}
      </div>
      {/* Info */}
      <div style={{ padding:"8px 10px 10px" }}>
        <div style={{ fontSize:"0.65rem", color:G.muted, fontWeight:600, marginBottom:2 }}>{p.unit || ""}</div>
        <div style={{ fontWeight:700, fontSize:"0.82rem", color:G.text, lineHeight:1.35, marginBottom:6, display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical", overflow:"hidden" }}>
          {p.name}
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:5, marginBottom:8 }}>
          <span style={{ fontWeight:800, fontSize:"0.9rem", color:G.text }}>₹{p.price}</span>
          {p.mrp > p.price && (
            <span style={{ fontSize:"0.7rem", color:G.muted, textDecoration:"line-through" }}>₹{p.mrp}</span>
          )}
        </div>
        {p.inStock !== false ? (
          <QtyBtn qty={qty} onAdd={onAdd} onDec={onDec}/>
        ) : (
          <div style={{ height:32, display:"flex", alignItems:"center", justifyContent:"center", background:"#f9fafb", borderRadius:8 }}>
            <span style={{ fontSize:"0.72rem", color:G.muted, fontWeight:600 }}>Unavailable</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Store Banner — Blinkit style scrolling pics ──
function StoreBanner({ store }) {
  const [idx, setIdx] = useState(0);

  // Combine store images: banner + images array
  const images = [
    ...(store?.bannerImage ? [store.bannerImage] : []),
    ...(store?.images       ? store.images        : []),
    ...(store?.image        ? [store.image]        : []),
  ].filter(Boolean);

  // Fallback banners if no images
  const FALLBACK = [
    { bg:"linear-gradient(135deg,#052e16,#166534,#22c55e)", title:"Fresh & Fast", sub:"Delivered in 10 minutes" },
    { bg:"linear-gradient(135deg,#1c1917,#78350f,#d97706)", title:"Best Prices",  sub:"No hidden charges ever"  },
    { bg:"linear-gradient(135deg,#0c1445,#1d4ed8,#60a5fa)", title:"Free Delivery",sub:"Orders above ₹199"       },
  ];

  useEffect(() => {
    const count = images.length || FALLBACK.length;
    if (count <= 1) return;
    const t = setInterval(() => setIdx(i => (i + 1) % count), 3500);
    return () => clearInterval(t);
  }, [images.length]);

  const total = images.length || FALLBACK.length;

  return (
    <div style={{ position:"relative", height:130, overflow:"hidden", margin:"10px 12px", borderRadius:16 }}>
      {images.length > 0 ? (
        images.map((src, i) => (
          <img key={i} src={src} alt="store" style={{
            position:"absolute", inset:0, width:"100%", height:"100%",
            objectFit:"cover", borderRadius:16,
            opacity: i === idx ? 1 : 0,
            transition:"opacity 0.6s ease",
          }}/>
        ))
      ) : (
        FALLBACK.map((fb, i) => (
          <div key={i} style={{
            position:"absolute", inset:0, background:fb.bg, borderRadius:16,
            display:"flex", flexDirection:"column", alignItems:"flex-start",
            justifyContent:"flex-end", padding:"14px 16px",
            opacity: i === idx ? 1 : 0,
            transition:"opacity 0.6s ease",
          }}>
            <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:"1.05rem", color:"#fff", letterSpacing:"-0.3px" }}>{fb.title}</div>
            <div style={{ fontSize:"0.72rem", color:"rgba(255,255,255,0.8)", marginTop:2 }}>{fb.sub}</div>
          </div>
        ))
      )}

      {/* Store info overlay */}
      {store && (
        <div style={{ position:"absolute", top:10, left:10, display:"flex", alignItems:"center", gap:7, background:"rgba(0,0,0,0.4)", backdropFilter:"blur(8px)", borderRadius:10, padding:"5px 10px" }}>
          <div style={{ width:8, height:8, borderRadius:"50%", background:store.isOpen !== false ? "#4ade80" : "#f87171" }}/>
          <span style={{ fontSize:"0.68rem", fontWeight:700, color:"#fff" }}>
            {store.isOpen !== false ? `Open · ${store.deliveryTime || 45} min` : "Closed"}
          </span>
        </div>
      )}

      {/* Dot indicators */}
      {total > 1 && (
        <div style={{ position:"absolute", bottom:8, left:0, right:0, display:"flex", justifyContent:"center", gap:5 }}>
          {Array.from({ length: total }).map((_, i) => (
            <div key={i} onClick={() => setIdx(i)}
              style={{ width: i === idx ? 18 : 5, height:5, borderRadius:100, background: i === idx ? "#fff" : "rgba(255,255,255,0.5)", cursor:"pointer", transition:"all 0.3s" }}/>
          ))}
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════
// MAIN PAGE
// ════════════════════════════════════════════════
export default function GroceryPage() {
  const navigate = useNavigate();
  const { cart, addToCart, increaseQty, decreaseQty } = useCart();

  const [selStore,      setSelStore]      = useState(null);
  const [allItems,      setAllItems]      = useState([]);

  // ── Category state ─────────────────────────────
  const [mainCats,      setMainCats]      = useState([]);  // from /api/categories
  const [activeMain,    setActiveMain]    = useState(null); // selected main cat id
  const [activeSub,     setActiveSub]     = useState(null); // selected sub cat id

  const [search,        setSearch]        = useState("");
  const [srchOpen,      setSrchOpen]      = useState(false);
  const [scrolled,      setScrolled]      = useState(false);
  const [loadingStores, setLoadingStores] = useState(true);
  const [loadingItems,  setLoadingItems]  = useState(false);
  const [storesError,   setStoresError]   = useState(null);

  const srchRef = useRef(null);

  // ── Scroll ────────────────────────────────────
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  // ── Load categories from backend ──────────────
  const loadCategories = async () => {
    try {
      const res  = await fetch(`${API}/categories`);
      const data = await res.json();
      if (data.success && data.categories?.length > 0) {
        setMainCats(data.categories);
        // ✅ FIX: Default "All" — koi category auto-select nahi
        setActiveMain(null);
        setActiveSub(null);
      }
    } catch {}
  };

  // ── Load store + products ─────────────────────
  const fetchNearbyProducts = async () => {
    setLoadingStores(true);
    setStoresError(null);
    try {
      let url = `${API}/grocery/products/nearby`;
      try {
        const pos = await new Promise((res, rej) =>
          navigator.geolocation.getCurrentPosition(res, rej, { timeout:5000 })
        );
        url = `${API}/grocery/products/nearby?lat=${pos.coords.latitude}&lng=${pos.coords.longitude}`;
      } catch {}

      const res  = await fetch(url);
      const data = await res.json();

      if (data.success) {
        if (!data.available) {
          setStoresError(data.message || "Aapke area mein delivery available nahi");
        } else {
          setSelStore(data.store);
          setAllItems(data.data || []);
        }
      } else {
        setStoresError(data.message || "Failed to load products");
      }
    } catch (e) {
      setStoresError("Backend se connect nahi hua: " + e.message);
    }
    setLoadingStores(false);
  };

  useEffect(() => {
    fetchNearbyProducts();
    loadCategories();
  }, []);

  // ── Derived: current sub categories ───────────
  const currentMainCat = mainCats.find(c => c._id === activeMain);
  const subCats        = currentMainCat?.subCategories || [];

  // ── Filtered products ─────────────────────────
  const filteredItems = allItems.filter(p => {
    const matchSearch = !search ||
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      p.description?.toLowerCase().includes(search.toLowerCase()) ||
      p.unit?.toLowerCase().includes(search.toLowerCase());

    // ✅ FIX: Search ho raha ho to category filter mat lagao — saare items search karo
    if (search) return matchSearch;

    // Category filter (sirf jab search nahi ho)
    let matchCat = true;
    if (activeSub) {
      matchCat = (p.subCategory?._id || p.subCategory) === activeSub ||
                 (p.subCategory?._id || p.subCategory)?.toString() === activeSub;
    } else if (activeMain) {
      matchCat = (p.mainCategory?._id || p.mainCategory) === activeMain ||
                 (p.mainCategory?._id || p.mainCategory)?.toString() === activeMain;
    }

    return matchSearch && matchCat;
  });

  const getQty = (pid) => {
    const item = cart.find(i => i._id === pid);
    return item ? item.qty : 0;
  };

  const totalItems = cart.reduce((s, i) => s + i.qty, 0);
  const totalAmt   = cart.reduce((s, i) => s + i.price * i.qty, 0);

  // Color for current main cat
  const mainColor = currentMainCat?.color || G.primary;

  return (
    <div style={{ fontFamily:"'Plus Jakarta Sans',system-ui,sans-serif", background:G.bg, minHeight:"100vh", color:G.text, overflowX:"hidden", paddingBottom: totalItems > 0 ? 120 : 80, display:"flex", flexDirection:"column" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,800;9..144,900&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
        ::-webkit-scrollbar{display:none}
        button,input{font-family:inherit;outline:none}
        @keyframes up{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}
        @keyframes spin{to{transform:rotate(360deg)}}
        @keyframes blink{0%,100%{opacity:1}50%{opacity:.5}}
        @keyframes slideUp{from{opacity:0;transform:translateY(24px)}to{opacity:1;transform:translateY(0)}}
        @keyframes shimmer{0%{background-position:-400px 0}100%{background-position:400px 0}}
        .pcard:hover{box-shadow:0 4px 20px rgba(0,0,0,0.1)!important}
        .skel{background:linear-gradient(90deg,#f0f0f0 25%,#e0e0e0 50%,#f0f0f0 75%);background-size:800px 100%;animation:shimmer 1.4s infinite linear;border-radius:8px;}
      `}</style>

      {/* ══ HEADER ══ */}
      <div style={{ position:"sticky", top:0, zIndex:200, background:scrolled?"rgba(255,255,255,0.97)":G.white, backdropFilter:"blur(20px)", boxShadow:scrolled?"0 2px 16px rgba(0,0,0,0.1)":`0 1px 0 ${G.border}`, transition:"all .2s" }}>
        <div style={{ padding:"11px 16px", display:"flex", alignItems:"center", gap:10 }}>
          <button onClick={() => navigate("/")} style={{ background:"none", border:"none", cursor:"pointer", display:"flex", padding:4, marginLeft:-4 }}>
            <ArrowLeft size={22} color={G.text}/>
          </button>
          <div style={{ flex:1 }}>
            <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:"1.2rem", letterSpacing:"-0.5px", color:G.text }}>
              {selStore ? selStore.name : "Grocery"}
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:4, marginTop:1 }}>
              {selStore ? (
                <>
                  <MapPin size={10} color={G.primary}/>
                  <span style={{ fontSize:"0.68rem", fontWeight:600, color:G.sub }}>{selStore.area}</span>
                  <span style={{ fontSize:"0.65rem", color:G.muted }}>· {selStore.deliveryTime || 45} min</span>
                </>
              ) : (
                <>
                  <div style={{ width:8, height:8, borderRadius:"50%", background:G.primary, animation:"blink 1.5s infinite" }}/>
                  <span style={{ fontSize:"0.68rem", fontWeight:700, color:G.primary }}>Delivery in 10–20 min</span>
                </>
              )}
            </div>
          </div>
          {selStore && (
            <button onClick={() => { setSrchOpen(true); setTimeout(() => srchRef.current?.focus(), 80); }}
              style={{ background:G.bg, border:`1px solid ${G.border}`, borderRadius:12, width:40, height:40, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer" }}>
              <Search size={18} color={G.sub}/>
            </button>
          )}
          <button onClick={() => navigate("/cart")} style={{ position:"relative", background:G.light, border:"none", borderRadius:12, width:40, height:40, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer" }}>
            <ShoppingCart size={18} color={G.primary}/>
            {totalItems > 0 && (
              <div style={{ position:"absolute", top:-4, right:-4, background:G.primary, color:"#fff", minWidth:17, height:17, borderRadius:100, fontSize:"0.58rem", fontWeight:800, display:"flex", alignItems:"center", justifyContent:"center", padding:"0 3px" }}>
                {totalItems > 9 ? "9+" : totalItems}
              </div>
            )}
          </button>
        </div>
      </div>

      {/* ══ SEARCH OVERLAY ══ */}
      {srchOpen && (
        <div onClick={() => setSrchOpen(false)} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:400 }}>
          <div onClick={e => e.stopPropagation()} style={{ background:G.white, padding:"14px 16px", boxShadow:"0 4px 24px rgba(0,0,0,0.15)" }}>
            <div style={{ display:"flex", alignItems:"center", gap:10, background:G.bg, border:`2px solid ${G.primary}`, borderRadius:14, padding:"10px 14px" }}>
              <Search size={17} color={G.primary}/>
              <input ref={srchRef} value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search groceries..."
                style={{ flex:1, border:"none", background:"transparent", fontSize:"0.92rem", color:G.text }}/>
              <button onClick={() => { setSearch(""); setSrchOpen(false); setActiveMain(null); setActiveSub(null); }} style={{ background:"none", border:"none", cursor:"pointer", display:"flex" }}>
                <X size={17} color={G.muted}/>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══ SKELETON LOADING — Blinkit style ══ */}
      {loadingStores && (
        <div style={{ display:"flex", flex:1 }}>
          {/* Sidebar skeleton */}
          <div style={{ width:82, flexShrink:0, background:G.white, borderRight:`1px solid ${G.border}`, padding:"10px 8px", display:"flex", flexDirection:"column", gap:14 }}>
            {[...Array(7)].map((_,i) => (
              <div key={i} style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:6 }}>
                <div className="skel" style={{ width:44, height:44, borderRadius:14 }}/>
                <div className="skel" style={{ width:38, height:8, borderRadius:4 }}/>
              </div>
            ))}
          </div>

          {/* Content skeleton */}
          <div style={{ flex:1, padding:"12px 12px 0" }}>
            {/* Category heading skel */}
            <div style={{ marginBottom:16 }}>
              <div className="skel" style={{ width:140, height:18, borderRadius:6, marginBottom:6 }}/>
              <div className="skel" style={{ width:80, height:11, borderRadius:4 }}/>
            </div>

            {/* Sub category chips skel */}
            <div style={{ display:"flex", gap:8, marginBottom:16, overflowX:"hidden" }}>
              {[...Array(4)].map((_,i) => (
                <div key={i} className="skel" style={{ width:80+i*10, height:30, borderRadius:100, flexShrink:0 }}/>
              ))}
            </div>

            {/* Product cards skeleton grid */}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
              {[...Array(6)].map((_,i) => (
                <div key={i} style={{ background:G.white, borderRadius:16, overflow:"hidden", border:`1px solid ${G.border}`, animation:`up .3s ${i*0.08}s both` }}>
                  {/* Image area */}
                  <div className="skel" style={{ height:110, borderRadius:0 }}/>
                  {/* Info */}
                  <div style={{ padding:"10px 10px 12px", display:"flex", flexDirection:"column", gap:7 }}>
                    <div className="skel" style={{ width:"60%", height:9, borderRadius:4 }}/>
                    <div className="skel" style={{ width:"100%", height:11, borderRadius:4 }}/>
                    <div className="skel" style={{ width:"75%", height:11, borderRadius:4 }}/>
                    <div style={{ display:"flex", alignItems:"center", gap:6, marginTop:2 }}>
                      <div className="skel" style={{ width:40, height:14, borderRadius:4 }}/>
                      <div className="skel" style={{ width:30, height:11, borderRadius:4 }}/>
                    </div>
                    <div className="skel" style={{ width:"100%", height:32, borderRadius:8, marginTop:2 }}/>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ══ ERROR ══ */}
      {!loadingStores && storesError && (
        <div style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center", flexDirection:"column", gap:12, padding:40, textAlign:"center" }}>
          <div style={{ fontSize:"3rem" }}>😔</div>
          <div style={{ fontWeight:800, fontSize:"1rem", color:G.text }}>Delivery Available Nahi</div>
          <div style={{ color:G.muted, fontSize:"0.82rem", lineHeight:1.6, maxWidth:260 }}>{storesError}</div>
          <button onClick={fetchNearbyProducts}
            style={{ background:G.primary, border:"none", borderRadius:12, padding:"11px 28px", color:"#fff", fontWeight:700, cursor:"pointer", display:"flex", alignItems:"center", gap:7, fontFamily:"inherit" }}>
            <RefreshCw size={15}/> Retry
          </button>
        </div>
      )}

      {/* ══ MAIN CONTENT ══ */}
      {!loadingStores && !storesError && selStore && (
        <div style={{ display:"flex", flex:1, alignItems:"flex-start" }}>

          {/* ── LEFT SIDEBAR — Main Categories ─────── */}
          <div style={{ width:82, flexShrink:0, position:"sticky", top:62, maxHeight:"calc(100vh - 62px)", overflowY:"auto", background:G.white, borderRight:`1px solid ${G.border}` }}>

            {/* All items option */}
            <div onClick={() => { setActiveMain(null); setActiveSub(null); }}
              style={{ padding:"14px 6px 12px", display:"flex", flexDirection:"column", alignItems:"center", gap:6, cursor:"pointer", background:!activeMain?G.light:"transparent", borderLeft:!activeMain?`3px solid ${G.primary}`:"3px solid transparent", transition:"all .15s" }}>
              <div style={{ width:44, height:44, borderRadius:14, background:!activeMain?G.mid:"#f1f5f9", display:"flex", alignItems:"center", justifyContent:"center" }}>
                <Store size={20} color={!activeMain?G.primary:"#64748b"} strokeWidth={1.8}/>
              </div>
              <div style={{ fontSize:"0.58rem", fontWeight:!activeMain?800:600, color:!activeMain?G.primary:G.sub, textAlign:"center", lineHeight:1.3 }}>All</div>
            </div>

            {/* Main categories from backend */}
            {mainCats.map(cat => {
              const isActive = activeMain === cat._id;
              const color    = cat.color || G.primary;
              const bg       = color + "20";
              return (
                <div key={cat._id} onClick={() => { setActiveMain(cat._id); setActiveSub(null); }}
                  style={{ padding:"14px 6px 12px", display:"flex", flexDirection:"column", alignItems:"center", gap:6, cursor:"pointer", background:isActive?G.light:"transparent", borderLeft:isActive?`3px solid ${color}`:"3px solid transparent", transition:"all .15s" }}>
                  <div style={{ width:44, height:44, borderRadius:14, background:isActive?bg:bg, display:"flex", alignItems:"center", justifyContent:"center", overflow:"hidden", transition:"all .15s", flexShrink:0 }}>
                    {cat.image
                      ? <img src={cat.image} alt={cat.name} style={{ width:44, height:44, objectFit:"cover", borderRadius:14 }} onError={e=>{e.target.style.display="none";e.target.nextSibling.style.display="flex"}}/>
                      : null}
                    <span style={{ fontSize:"1.3rem", display:cat.image?"none":"flex" }}>{cat.icon || "🛒"}</span>
                  </div>
                  <div style={{ fontSize:"0.58rem", fontWeight:isActive?800:600, color:isActive?color:G.sub, textAlign:"center", lineHeight:1.3, maxWidth:70, overflow:"hidden" }}>
                    {cat.name.split(" ")[0]}
                  </div>
                </div>
              );
            })}
          </div>

          {/* ── RIGHT CONTENT ───────────────────────── */}
          <div style={{ flex:1, minWidth:0 }}>

            {/* ── Store Banner / Gallery (Blinkit style scrolling) ── */}
            <StoreBanner store={selStore} />

            {/* ── Sub Categories horizontal strip ────── */}
            {activeMain && subCats.length > 0 && (
              <div style={{ background:G.white, borderBottom:`1px solid ${G.border}`, position:"sticky", top:62, zIndex:100 }}>
                <div style={{ display:"flex", overflowX:"auto", padding:"10px 12px", gap:8 }}>
                  {/* All sub */}
                  <button onClick={() => setActiveSub(null)}
                    style={{ flexShrink:0, padding:"6px 14px", borderRadius:100, border:`1.5px solid ${!activeSub ? mainColor : G.border}`, background:!activeSub ? mainColor : G.white, color:!activeSub ? "#fff" : G.sub, fontWeight:700, fontSize:"0.75rem", cursor:"pointer", whiteSpace:"nowrap", transition:"all .18s" }}>
                    All {currentMainCat?.name?.split(" ")[0]}
                  </button>
                  {subCats.map(sub => {
                    const isActive = activeSub === sub._id;
                    return (
                      <button key={sub._id} onClick={() => setActiveSub(isActive ? null : sub._id)}
                        style={{ flexShrink:0, display:"flex", alignItems:"center", gap:6, padding:"6px 14px", borderRadius:100, border:`1.5px solid ${isActive ? mainColor : G.border}`, background:isActive ? mainColor + "15" : G.white, color:isActive ? mainColor : G.sub, fontWeight:isActive?700:600, fontSize:"0.75rem", cursor:"pointer", whiteSpace:"nowrap", transition:"all .18s" }}>
                        {sub.image
                          ? <img src={sub.image} alt="" style={{ width:20, height:20, objectFit:"cover", borderRadius:6 }} onError={e=>e.target.style.display="none"}/>
                          : <span style={{ fontSize:"1rem" }}>{sub.icon || "📦"}</span>
                        }
                        {sub.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── Category heading ───────────────────── */}
            <div style={{ padding:"12px 12px 8px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <div>
                <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:"1rem", color:G.text, display:"flex", alignItems:"center", gap:8 }}>
                  {activeSub
                    ? <><span style={{ fontSize:"1.2rem" }}>{subCats.find(s=>s._id===activeSub)?.icon}</span> {subCats.find(s=>s._id===activeSub)?.name}</>
                    : activeMain
                      ? <><span style={{ fontSize:"1.2rem" }}>{currentMainCat?.icon}</span> {currentMainCat?.name}</>
                      : "All Products"
                  }
                </div>
                <div style={{ fontSize:"0.68rem", color:G.muted, marginTop:2 }}>{filteredItems.length} items</div>
              </div>
            </div>

            {/* ── Products Grid ──────────────────────── */}
            {filteredItems.length === 0 ? (
              <div style={{ textAlign:"center", padding:"60px 20px" }}>
                <Package size={52} color={G.border} strokeWidth={1}/>
                <div style={{ fontSize:"0.95rem", fontWeight:700, color:G.sub, marginTop:14 }}>
                  {allItems.length === 0 ? "Is store mein abhi koi items nahi" : "No items found"}
                </div>
                <div style={{ fontSize:"0.8rem", color:G.muted, marginTop:4 }}>
                  {allItems.length === 0 ? "Admin se items add karne ko bolein" : "Try another category"}
                </div>
              </div>
            ) : (
              /* ── When "All" selected — group by main category ── */
              activeMain === null ? (
                mainCats.map(cat => {
                  const catItems = allItems.filter(p =>
                    (p.mainCategory?._id || p.mainCategory)?.toString() === cat._id.toString()
                  );
                  if (!catItems.length) return null;
                  const color = cat.color || G.primary;
                  return (
                    <div key={cat._id} style={{ marginBottom:24 }}>
                      {/* Section header */}
                      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"12px 12px 8px" }}>
                        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                          <div style={{ width:34, height:34, borderRadius:10, background:color+"20", display:"flex", alignItems:"center", justifyContent:"center", overflow:"hidden", flexShrink:0 }}>
                            {cat.image
                              ? <img src={cat.image} alt={cat.name} style={{ width:34, height:34, objectFit:"cover", borderRadius:10 }} onError={e=>{e.target.style.display="none";e.target.nextSibling.style.display="flex"}}/>
                              : null}
                            <span style={{ fontSize:"1.1rem", display:cat.image?"none":"flex" }}>{cat.icon||"🛒"}</span>
                          </div>
                          <div>
                            <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:"0.95rem", color:G.text }}>{cat.name}</div>
                            <div style={{ fontSize:"0.65rem", color:G.muted }}>{catItems.length} items</div>
                          </div>
                        </div>
                        <button onClick={() => { setActiveMain(cat._id); setActiveSub(null); }}
                          style={{ background:"none", border:"none", cursor:"pointer", color:color, fontWeight:700, fontSize:"0.75rem", display:"flex", alignItems:"center", gap:4 }}>
                          See all <ChevronRight size={13}/>
                        </button>
                      </div>
                      {/* Sub category chips */}
                      {cat.subCategories?.length > 0 && (
                        <div style={{ display:"flex", overflowX:"auto", padding:"0 12px 8px", gap:6 }}>
                          {cat.subCategories.map(sub => (
                            <button key={sub._id} onClick={() => { setActiveMain(cat._id); setActiveSub(sub._id); }}
                              style={{ flexShrink:0, display:"flex", alignItems:"center", gap:5, padding:"4px 12px", borderRadius:100, border:`1px solid ${G.border}`, background:G.white, color:G.sub, fontWeight:600, fontSize:"0.72rem", cursor:"pointer", whiteSpace:"nowrap" }}>
                              {sub.image
                                ? <img src={sub.image} alt="" style={{ width:18, height:18, objectFit:"cover", borderRadius:5 }} onError={e=>e.target.style.display="none"}/>
                                : <span>{sub.icon||"📦"}</span>
                              }
                              {sub.name}
                            </button>
                          ))}
                        </div>
                      )}
                      {/* Products */}
                      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, padding:"0 12px" }}>
                        {catItems.slice(0, 6).map(p => (
                          <ProductCard key={p._id} p={p} color={color}
                            qty={getQty(p._id)}
                            onAdd={() => getQty(p._id)===0 ? addToCart({...p, storeId:selStore._id, storeName:selStore.name, serviceType:'grocery', isVeg:true}) : increaseQty(p._id)}
                            onDec={() => decreaseQty(p._id)}/>
                        ))}
                      </div>
                      {catItems.length > 6 && (
                        <div style={{ textAlign:"center", padding:"12px 0 4px" }}>
                          <button onClick={() => { setActiveMain(cat._id); setActiveSub(null); }}
                            style={{ background:"none", border:`1px solid ${color}`, borderRadius:100, padding:"7px 20px", color:color, fontWeight:700, fontSize:"0.78rem", cursor:"pointer" }}>
                            +{catItems.length - 6} more items
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                /* ── Filtered by main/sub cat ── */
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, padding:"0 12px 12px" }}>
                  {filteredItems.map((p, i) => (
                    <ProductCard key={p._id} p={p} color={mainColor}
                      qty={getQty(p._id)}
                      onAdd={() => getQty(p._id)===0 ? addToCart({...p, storeId:selStore._id, storeName:selStore.name, serviceType:'grocery', isVeg:true}) : increaseQty(p._id)}
                      onDec={() => decreaseQty(p._id)}/>
                  ))}
                </div>
              )
            )}

            {/* Why shop with us */}
            <div style={{ margin:"8px 12px 20px", background:G.white, borderRadius:16, padding:"14px", border:`1px solid ${G.border}` }}>
              <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:"0.9rem", color:G.text, marginBottom:12 }}>Why shop with us?</div>
              {[
                { icon:Zap,          label:"10 min delivery",    sub:"Fastest in Akola",       color:G.primary   },
                { icon:CheckCircle,  label:"100% fresh",         sub:"Quality guaranteed",     color:"#16a34a"   },
                { icon:BadgePercent, label:"Best prices",        sub:"No hidden charges",      color:"#2563eb"   },
                { icon:Truck,        label:"Free above ₹199",    sub:"Free delivery every time",color:"#ea580c"  },
              ].map((w, i) => (
                <div key={i} style={{ display:"flex", alignItems:"center", gap:10, marginBottom:i<3?10:0 }}>
                  <div style={{ width:34, height:34, borderRadius:10, background:w.color+"15", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                    <w.icon size={17} color={w.color} strokeWidth={1.8}/>
                  </div>
                  <div>
                    <div style={{ fontSize:"0.78rem", fontWeight:700, color:G.text }}>{w.label}</div>
                    <div style={{ fontSize:"0.66rem", color:G.muted }}>{w.sub}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ══ FLOATING CART BAR ══ */}
      {totalItems > 0 && (
        <div style={{ position:"fixed", bottom:72, left:12, right:12, zIndex:300, animation:"slideUp .3s ease" }}>
          <button onClick={() => navigate("/cart")}
            style={{ width:"100%", background:"linear-gradient(135deg,#0c831f,#1aab2f)", border:"none", borderRadius:16, padding:"14px 18px", display:"flex", alignItems:"center", justifyContent:"space-between", cursor:"pointer", boxShadow:`0 8px 32px ${G.primary}50` }}>
            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
              <div style={{ background:"rgba(255,255,255,0.2)", borderRadius:8, padding:"4px 10px", display:"flex", alignItems:"center", gap:5 }}>
                <ShoppingCart size={15} color="#fff"/>
                <span style={{ color:"#fff", fontWeight:800, fontSize:"0.82rem" }}>{totalItems} item{totalItems>1?"s":""}</span>
              </div>
              <span style={{ color:"rgba(255,255,255,0.85)", fontSize:"0.78rem", fontWeight:600 }}>₹{totalAmt}</span>
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:6 }}>
              <span style={{ color:"#fff", fontWeight:800, fontSize:"0.85rem" }}>View Cart</span>
              <ChevronRight size={16} color="#fff"/>
            </div>
          </button>
        </div>
      )}

      {/* ══ BOTTOM NAV ══ */}
      <div style={{ position:"fixed", bottom:0, left:0, right:0, background:"rgba(255,255,255,0.97)", backdropFilter:"blur(24px)", borderTop:`1px solid ${G.border}`, display:"flex", zIndex:200, boxShadow:"0 -4px 24px rgba(0,0,0,0.07)" }}>
        {NAV.map(({ icon:Icon, label, route, cart:isCart }) => {
          const isActive = route === "/grocery";
          return (
            <button key={route} onClick={() => navigate(route)}
              style={{ flex:1, background:"none", border:"none", padding:"10px 0 8px", cursor:"pointer", display:"flex", flexDirection:"column", alignItems:"center", gap:3, position:"relative" }}>
              <div style={{ position:"relative" }}>
                <Icon size={22} color={isActive?G.primary:"#9ca3af"} strokeWidth={isActive?2.3:1.8}/>
                {isCart && totalItems > 0 && (
                  <div style={{ position:"absolute", top:-5, right:-7, background:"#f97316", color:"#fff", minWidth:16, height:16, borderRadius:100, fontSize:"0.55rem", fontWeight:800, display:"flex", alignItems:"center", justifyContent:"center", padding:"0 3px" }}>
                    {totalItems > 9 ? "9+" : totalItems}
                  </div>
                )}
              </div>
              <span style={{ fontSize:"0.6rem", fontWeight:isActive?800:500, color:isActive?G.primary:"#9ca3af" }}>{label}</span>
              {isActive && <div style={{ position:"absolute", bottom:0, width:22, height:3, background:G.primary, borderRadius:100 }}/>}
            </button>
          );
        })}
      </div>
    </div>
  );
}